import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Play, ArrowRight, Settings, RotateCcw, CheckCircle, XCircle, Box, Zap } from 'lucide-react';
import { useAcademyStore } from '../../store/academyStore';

type Operation = {
    id: string;
    label: string;
    func: (x: any) => any;
    icon: string;
};

type Level = {
    id: number;
    name: string;
    description: string;
    inputs: any[];
    expectedOutputs: any[];
    availableOps: Operation[];
    hint: string;
};

const LEVELS: Level[] = [
    {
        id: 1,
        name: "The Doubler",
        description: "Configure the machine to double the input numbers.",
        inputs: [2, 5, 10, 50],
        expectedOutputs: [4, 10, 20, 100],
        availableOps: [
            { id: 'add1', label: '+ 1', func: (x: number) => x + 1, icon: '➕' },
            { id: 'mult2', label: '× 2', func: (x: number) => x * 2, icon: '❌' },
            { id: 'sub1', label: '- 1', func: (x: number) => x - 1, icon: '➖' },
        ],
        hint: "We need to multiply by 2."
    },
    {
        id: 2,
        name: "The Greeter",
        description: "Make the machine say 'Hello' to everyone.",
        inputs: ["Bob", "Alice", "Gorilla"],
        expectedOutputs: ["Hello Bob", "Hello Alice", "Hello Gorilla"],
        availableOps: [
            { id: 'upper', label: 'UPPERCASE', func: (x: string) => x.toUpperCase(), icon: '🔠' },
            { id: 'hello', label: '"Hello " + x', func: (x: string) => "Hello " + x, icon: '👋' },
            { id: 'bye', label: '"Bye " + x', func: (x: string) => "Bye " + x, icon: '👋' },
        ],
        hint: "Combine the string 'Hello ' with the input."
    },
    {
        id: 3,
        name: "Even Checker",
        description: "Check if the numbers are even. Return true or false.",
        inputs: [2, 3, 10, 7],
        expectedOutputs: [true, false, true, false],
        availableOps: [
            { id: 'gt5', label: '> 5', func: (x: number) => x > 5, icon: '⚖️' },
            { id: 'mod2', label: 'x % 2 === 0', func: (x: number) => x % 2 === 0, icon: '➗' },
            { id: 'mult2', label: '× 2', func: (x: number) => x * 2, icon: '✖️' }
        ],
        hint: "Modulo operator (%) gives the remainder. Even numbers have remainder 0 when divided by 2."
    }
];

export const FunctionFactory = () => {
    const { completeModule } = useAcademyStore();
    const [currentLevel, setCurrentLevel] = useState(0);
    const [selectedOp, setSelectedOp] = useState<Operation | null>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [results, setResults] = useState<any[]>([]);
    const [status, setStatus] = useState<'idle' | 'running' | 'success' | 'fail'>('idle');

    const level = LEVELS[currentLevel];

    useEffect(() => {
        resetLevel();
    }, [currentLevel]);

    const resetLevel = () => {
        setSelectedOp(null);
        setIsRunning(false);
        setResults([]);
        setStatus('idle');
    };

    const runMachine = async () => {
        if (!selectedOp || isRunning) return;
        setIsRunning(true);
        setStatus('running');
        setResults([]);

        let allCorrect = true;
        const newResults = [];

        for (let i = 0; i < level.inputs.length; i++) {
            const input = level.inputs[i];

            // Visual delay for "processing"
            await new Promise(r => setTimeout(r, 800));

            let output;
            try {
                output = selectedOp.func(input);
            } catch (e) {
                output = "ERROR";
            }

            const isCorrect = JSON.stringify(output) === JSON.stringify(level.expectedOutputs[i]);
            if (!isCorrect) allCorrect = false;

            newResults.push({ input, output, isCorrect });
            setResults([...newResults]);
        }

        setStatus(allCorrect ? 'success' : 'fail');
        setIsRunning(false);

        if (allCorrect && currentLevel === LEVELS.length - 1) {
            completeModule('functions-scope', 100);
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 font-sans text-white flex flex-col">
            {/* Header */}
            <div className="bg-slate-800 border-b border-slate-700 px-8 py-4 flex justify-between items-center sticky top-0 z-50">
                <Link to="/" className="flex items-center gap-2 text-slate-400 font-bold hover:text-purple-400 transition-colors">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="flex items-center gap-4">
                    <span className="text-slate-400 font-bold">Level {currentLevel + 1}: {level.name}</span>
                    <div className="flex gap-1">
                        {LEVELS.map((l, i) => (
                            <div
                                key={l.id}
                                className={`w-3 h-3 rounded-full ${i < currentLevel ? 'bg-green-500' : i === currentLevel ? 'bg-purple-500 animate-pulse' : 'bg-slate-700'}`}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex-1 max-w-6xl mx-auto w-full p-8 flex flex-col lg:flex-row gap-8">

                {/* Control Panel (Left) */}
                <div className="w-full lg:w-1/3 flex flex-col gap-6">
                    <div className="bg-slate-800 p-6 rounded-3xl border border-slate-700 shadow-xl">
                        <h1 className="text-3xl font-black text-purple-400 mb-2 font-comic tracking-wide">Function Factory</h1>
                        <p className="text-slate-300 mb-6">{level.description}</p>

                        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700 mb-6">
                            <h3 className="text-sm font-bold text-slate-500 uppercase mb-3 flex items-center gap-2">
                                <Settings size={14} /> Configure Machine Logic
                            </h3>
                            <div className="grid grid-cols-1 gap-3">
                                {level.availableOps.map(op => (
                                    <button
                                        key={op.id}
                                        onClick={() => !isRunning && setSelectedOp(op)}
                                        disabled={isRunning}
                                        className={`p-4 rounded-xl border-2 transition-all flex items-center gap-4 text-left group ${selectedOp?.id === op.id
                                                ? 'bg-purple-500 border-purple-400 shadow-lg shadow-purple-500/20'
                                                : 'bg-slate-800 border-slate-600 hover:border-slate-500 hover:bg-slate-750'
                                            }`}
                                    >
                                        <span className="text-2xl">{op.icon}</span>
                                        <div>
                                            <div className="font-mono font-bold text-lg">{op.label}</div>
                                            <div className="text-xs opacity-60">Operation</div>
                                        </div>
                                        {selectedOp?.id === op.id && <CheckCircle className="ml-auto" />}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <button
                                onClick={resetLevel}
                                disabled={isRunning}
                                className="btn bg-slate-700 hover:bg-slate-600 text-slate-300"
                            >
                                <RotateCcw size={20} />
                            </button>
                            {status === 'success' ? (
                                <button
                                    onClick={() => currentLevel < LEVELS.length - 1 && setCurrentLevel(c => c + 1)}
                                    className="btn flex-1 bg-green-500 hover:bg-green-600 text-white shadow-lg shadow-green-500/30"
                                >
                                    Next Factory <ArrowRight />
                                </button>
                            ) : (
                                <button
                                    onClick={runMachine}
                                    disabled={!selectedOp || isRunning}
                                    className={`btn flex-1 bg-purple-500 hover:bg-purple-600 text-white shadow-lg shadow-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                    {isRunning ? 'Processing...' : 'Start Machine'} <Play fill="currentColor" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Visual Assembly Line (Right) */}
                <div className="flex-1 bg-slate-800 rounded-3xl border border-slate-700 shadow-2xl relative overflow-hidden flex flex-col">
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10" />

                    {/* Machine Header */}
                    <div className="bg-slate-900/80 p-4 border-b border-slate-700 flex justify-between items-center relative z-10">
                        <div className="flex items-center gap-2">
                            <Box className="text-purple-400" />
                            <span className="font-mono font-bold text-slate-400">FACTORY_V{currentLevel}.0</span>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${status === 'running' ? 'bg-yellow-500/20 text-yellow-400 animate-pulse' :
                                status === 'success' ? 'bg-green-500/20 text-green-400' :
                                    status === 'fail' ? 'bg-red-500/20 text-red-400' : 'bg-slate-700 text-slate-400'
                            }`}>
                            <div className={`w-2 h-2 rounded-full ${status === 'running' ? 'bg-yellow-400' :
                                    status === 'success' ? 'bg-green-400' :
                                        status === 'fail' ? 'bg-red-400' : 'bg-slate-400'
                                }`} />
                            {status.toUpperCase()}
                        </div>
                    </div>

                    {/* Conveyor Belt Area */}
                    <div className="flex-1 relative flex flex-col items-center justify-center py-12 gap-8">
                        {/* The Machine Core */}
                        <div className="relative z-10">
                            <motion.div
                                animate={isRunning ? { scale: [1, 1.05, 1] } : {}}
                                transition={{ repeat: Infinity, duration: 0.5 }}
                                className="w-48 h-48 bg-slate-700 rounded-3xl border-8 border-slate-600 shadow-2xl flex items-center justify-center relative"
                            >
                                <div className="absolute inset-0 bg-purple-500/10 rounded-2xl animate-pulse" />
                                {selectedOp ? (
                                    <div className="text-center">
                                        <div className="text-4xl mb-2">{selectedOp.icon}</div>
                                        <div className="font-mono font-bold text-slate-200">{selectedOp.label}</div>
                                    </div>
                                ) : (
                                    <div className="text-slate-500 font-bold text-center px-4">Select Logic</div>
                                )}

                                {/* Piston Animation (Decor) */}
                                <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-16 h-8 bg-slate-600 rounded-t-lg" />
                                <motion.div
                                    animate={isRunning ? { height: [0, 20, 0] } : { height: 0 }}
                                    transition={{ repeat: Infinity, duration: 0.4 }}
                                    className="absolute -top-12 left-1/2 -translate-x-1/2 w-8 bg-slate-400"
                                />
                            </motion.div>
                        </div>

                        {/* Tracks */}
                        <div className="absolute top-1/2 left-0 right-0 h-4 bg-slate-900 border-y border-slate-700 -z-0">
                            <div className="absolute inset-0 flex justify-between px-2">
                                {[...Array(20)].map((_, i) => (
                                    <div key={i} className="w-1 h-full bg-slate-800" />
                                ))}
                            </div>
                        </div>

                        {/* Items Flow */}
                        <div className="w-full flex justify-center gap-4 min-h-[100px]">
                            <AnimatePresence>
                                {results.map((res, i) => (
                                    <motion.div
                                        key={`res-${i}`}
                                        initial={{ x: -100, opacity: 0, scale: 0.5 }}
                                        animate={{ x: 0, opacity: 1, scale: 1 }}
                                        transition={{ type: 'spring' }}
                                        className={`p-4 rounded-xl border-2 flex flex-col items-center min-w-[100px] bg-slate-800 ${res.isCorrect ? 'border-green-500 shadow-green-500/20 shadow-lg' : 'border-red-500 shadow-red-500/20 shadow-lg'
                                            }`}
                                    >
                                        <div className="text-slate-400 text-xs uppercase font-bold mb-1">In: {JSON.stringify(res.input)}</div>
                                        <div className="text-2xl mb-1">⬇️</div>
                                        <div className={`font-mono font-bold text-lg ${res.isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                                            {JSON.stringify(res.output)}
                                        </div>
                                        {!res.isCorrect && (
                                            <div className="mt-1 text-xs text-red-300">Expected: {JSON.stringify(level.expectedOutputs[i])}</div>
                                        )}
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
